import { useCallback, useEffect, useState } from 'react';

import {
  type AdsCreativeRequest,
  contentPath,
  dismissAdsCreativeRequest,
  fetchAdsCreativeRequests,
} from './api.ts';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { describeError } from './data.tsx';
import { Link, navigate, newContentFromRequestPath } from './router.tsx';
import { localTime } from './schedule.ts';
import { useToast } from './toast.tsx';
import { Badge, Facts, Notice, Panel } from './ui.tsx';

/**
 * Creative requests (meta-ads-agent US-019): the new creative a review asked
 * for. The Ads overview lists the open and started ones with **Start content**
 * (the New Content page, pre-filled) and **Dismiss**; the run detail shows the
 * ones its review filed with the same facts.
 */

/** How often the list is re-read: a review may file new requests at any time. */
const POLL_MS = 60_000;

export function creativeRequestStatusTone(status: AdsCreativeRequest['status']): 'ready' | 'active' | 'neutral' {
  return status === 'open' ? 'ready' : status === 'started' ? 'active' : 'neutral';
}

/** What the agent asked for: concept as the heading, then angle, format, ratios, hook and why. */
export function CreativeRequestFacts({ request }: { readonly request: AdsCreativeRequest }) {
  return (
    <Facts
      items={[
        ...(request.angle === null ? [] : [{ label: 'Angle', value: request.angle }]),
        ...(request.format === null ? [] : [{ label: 'Format', value: request.format === 'video' ? 'Video' : 'Image' }]),
        ...(request.aspectRatios.length === 0
          ? []
          : [{ label: 'Aspect ratios', value: request.aspectRatios.join(', '), mono: true }]),
        ...(request.hook === null
          ? []
          : [{ label: request.format === 'video' ? 'Hook (first 3 seconds)' : 'Hook', value: request.hook }]),
        ...(request.notes === null ? [] : [{ label: 'Why', value: request.notes }]),
      ]}
    />
  );
}

export function CreativeRequestsPanel() {
  const toast = useToast();
  const [requests, setRequests] = useState<AdsCreativeRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<AdsCreativeRequest | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback((signal?: AbortSignal): void => {
    fetchAdsCreativeRequests(signal)
      .then((next) => {
        setRequests(next.requests);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (signal?.aborted !== true) setError(describeError(cause));
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const timer = window.setInterval(() => {
      load(controller.signal);
    }, POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [load]);

  const dismiss = (): void => {
    if (dismissing === null) return;
    setBusy(true);
    dismissAdsCreativeRequest(dismissing.id)
      .then(() => {
        toast.ok('Request dismissed. The next review sees that you declined it.');
        setDismissing(null);
        load();
      })
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
        setDismissing(null);
        load();
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const open = requests?.filter((request) => request.status === 'open').length ?? 0;
  return (
    <Panel
      title="Creative requests"
      icon="image"
      meta={open === 0 ? undefined : <Badge tone="wait">{String(open)} open</Badge>}
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {requests !== null && requests.length === 0 && (
        <p className="muted">The agent has not asked for new creative.</p>
      )}
      {requests !== null && requests.length > 0 && (
        <ul className="ads-requests">
          {requests.map((request) => (
            <li key={request.id} className="ads-request">
              <div className="ads-request__head">
                <strong>{request.concept}</strong>
                <Badge tone={creativeRequestStatusTone(request.status)}>{request.status}</Badge>
                <span className="muted">{localTime(request.createdAt)}</span>
              </div>
              <CreativeRequestFacts request={request} />
              <div className="ads-request__actions">
                {request.status === 'open' ? (
                  <>
                    <button
                      type="button"
                      className="button button--primary"
                      onClick={() => {
                        navigate(newContentFromRequestPath(request.id));
                      }}
                    >
                      Start content
                    </button>
                    <button
                      type="button"
                      className="button button--quiet button--danger"
                      onClick={() => {
                        setDismissing(request);
                      }}
                    >
                      Dismiss
                    </button>
                  </>
                ) : request.contentId === null ? (
                  <span className="muted">Its content was deleted.</span>
                ) : (
                  <Link href={contentPath(request.contentId)}>
                    Made as {request.contentName ?? 'a content'}
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={dismissing !== null}
        title="Dismiss this request?"
        confirmLabel="Dismiss"
        busyLabel="Dismissing…"
        danger
        busy={busy}
        onConfirm={dismiss}
        onCancel={() => {
          setDismissing(null);
        }}
      >
        <p>
          {dismissing?.concept} is taken off the list. The next review is told you declined it, so it does not ask
          for the same concept again.
        </p>
      </ConfirmDialog>
    </Panel>
  );
}
